/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNav } from '../../hooks/nav';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/close.svg';
import { ReactComponent as LoadingIcon } from '../../assets/images/loading-white.svg';
import image1 from '../../assets/images/goodPhoto1.jpg';
import image2 from '../../assets/images/goodPhoto2.png';
import image3 from '../../assets/images/goodPhoto3.png';
import image4 from '../../assets/images/badPhoto1.jpg';
import image5 from '../../assets/images/badPhoto2.jpg';
import image6 from '../../assets/images/badPhoto3.jpg';
import { useStore } from 'src/onboarding/store/store';
import { useMutation } from '@tanstack/react-query';
import { AiService } from 'src/onboarding/services/ai.service';
import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';
import { getLocalizedText } from 'core/utils/localized';
import './style.scss';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { t } from '@web-solutions/module-localization';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import Button from 'src/onboarding/components/ui/button/Button';
import { ImageUploadResponse } from 'src/onboarding/types/api';
import { FileService } from 'src/onboarding/services/file.service';
import FaceScanOverlay from 'src/onboarding/components/FaceScanOverlay/FaceScanOverlay';
import getLocale from 'src/localization/get-locale';

const SCAN_DURATION = 4000;
const FILE_PROCESSING_TIMEOUT = 5000;

interface Landmark {
  type: string;
  x: number;
  y: number;
}

interface BoundingBox {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
}

interface FaceLandmarkData {
  boundingBox: BoundingBox;
  landmarks: Landmark[];
}

function Page() {
  const { onNext, onPrev, currentIndex } = useNav();
  const {
    setShowPhotoProcessing,
    setPhotoProcessingUuid,
    setPhotoError,
    setGeneratedPhotoUrl,
    setImageUrl,
    setFaceScanBase64,
    setFaceData,
    faceData,
  } = useStore();
  const locale = getLocale();

  const [image, setImage] = useState<File | null>(null);
  const [imageBase64, setImageBase64] = useState('');

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFaceScan, setShowFaceScan] = useState(false);
  const [hideScanLine, setHideScanLine] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const { isShowedSkipButton, showFaceScan: faceScanEnabled, ltsPhotoExample } = useRemoteConfig();
  const [showErrorPopUp, setShowErrorPopUp] = useState(false);

  const [hasError, setHasError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [filesError, setFilesError] = useState(false);
  const [faceLandmarkData, setFaceLandmarkData] = useState<FaceLandmarkData | null>(null);
  const [showFacePoints, setShowFacePoints] = useState(true);
  const [hasTrackedFaceScan, setHasTrackedFaceScan] = useState(false);
  const [faceLandmarksError, setFaceLandmarksError] = useState('');

  // Refs для скрытых инпутов
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (image) {
      setGeneratedPhotoUrl(null);
      setPhotoProcessingUuid('');
      setHasError(false);
    }
  }, [image]);

  useEffect(() => {
    if (faceScanEnabled && showFaceScan && isImageLoaded && !isLoading) {
      if (!hasTrackedFaceScan) {
        Analytics.trackEvent('face-scan', 'show');
        setHasTrackedFaceScan(true);
      }
      const timer = setTimeout(() => {
        if (hasError) {
          setShowErrorPopUp(true);
          Analytics.trackEvent('face-scan-error', 'show');
        }
        setHideScanLine(true);
        setShowFacePoints(true);

        setShowPhotoProcessing(true);
      }, SCAN_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showFaceScan, hasError, isImageLoaded, faceScanEnabled, hasTrackedFaceScan]);

  useEffect(() => {
    if (!showFaceScan) {
      setHasTrackedFaceScan(false);
    }
  }, [showFaceScan]);

  useEffect(() => {
    document.body.style.overflow = showFaceScan || filesError ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showFaceScan, filesError]);

  useEffect(() => {
    if (isBlurred && hideScanLine && showFaceScan && !showErrorPopUp) {
      const redirectTimer = setTimeout(() => {
        onNext();
      }, SCAN_DURATION);

      return () => clearTimeout(redirectTimer);
    }
  }, [isBlurred, hideScanLine, showFaceScan, showErrorPopUp]);

  const getOriginalImageSize = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);
    });
  };

  const handleChangeImage = async (element) => {
    setIsOpen(false);
    setIsImageLoaded(false);
    setHasTrackedFaceScan(false);

    const originalFile = element.files[0];

    if (!originalFile) return;

    try {
      setHideScanLine(false);
      setIsBlurred(false);
      setImage(null);
      setShowFacePoints(false);
      setIsLoading(true);

      const { correctedFile, base64 } = await fixImageOrientation(originalFile);

      setImage(correctedFile);
      setImageBase64(base64);
      setFaceScanBase64(base64);

      Analytics.trackEvent('user_loads', 'photo');

      handleFaceScan(correctedFile);
      setIsLoading(false);
    } catch (error) {
      console.error('Error processing image:', error);
      setPhotoError(true);
      setGeneratedPhotoUrl(null);
      setHasError(true);
      setIsLoading(false);
      setFilesError(true);

      Analytics.trackEvent('files-upload', 'error');
    } finally {
      element.value = null;
    }
  };

  const isHeicFormat = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    const isHeicByExtension = fileName.endsWith('.heic') || fileName.endsWith('.heif');

    const isHeicByMime = mimeType === 'image/heic' || mimeType === 'image/heif';

    if (mimeType === 'image/jpeg') {
      return false;
    }

    if (!mimeType) {
      return isHeicByExtension;
    }

    return isHeicByExtension || isHeicByMime;
  };

  const fixImageOrientation = async (
    file: File
  ): Promise<{ correctedFile: File; base64: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        if (isHeicFormat(file)) {
          try {
            const heic2any = (await import('heic2any')).default;

            const convertedBlob = (await heic2any({
              blob: file,
              toType: 'image/jpeg',
              quality: 0.95,
            })) as Blob;

            const fileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
            const convertedFile = new File([convertedBlob], fileName, {
              type: 'image/jpeg',
            });

            const base64 = await new Promise<string>((resolveBase64, rejectBase64) => {
              const reader = new FileReader();
              reader.onload = (e) => resolveBase64(e.target?.result as string);
              reader.onerror = rejectBase64;
              reader.readAsDataURL(convertedBlob);
            });

            resolve({ correctedFile: convertedFile, base64 });
            return;
          } catch (heicError) {
            if (heicError.message && heicError.message.includes('already browser readable')) {
              // Продолжаем обработку
            } else {
              throw heicError;
            }
          }
        }

        // Просто конвертируем в JPEG без изменения ориентации
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Сохраняем оригинальные размеры
            const width = img.width;
            const height = img.height;

            // Устанавливаем размеры канваса как у оригинального изображения
            canvas.width = width;
            canvas.height = height;

            // Рисуем изображение как есть, без поворотов
            ctx?.drawImage(img, 0, 0, width, height);

            const base64 = canvas.toDataURL('image/jpeg', 0.95);

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  // Fallback: возвращаем оригинальный файл
                  const fallbackReader = new FileReader();
                  fallbackReader.onload = (fallbackEvent) => {
                    resolve({
                      correctedFile: file,
                      base64: fallbackEvent.target?.result as string,
                    });
                  };
                  fallbackReader.readAsDataURL(file);
                  return;
                }

                const fileName =
                  file.name.toLowerCase().endsWith('.jpg') ||
                  file.name.toLowerCase().endsWith('.jpeg')
                    ? file.name
                    : file.name.replace(/\.[^/.]+$/, '') + '.jpg';

                const correctedFile = new File([blob], fileName, {
                  type: 'image/jpeg',
                });

                resolve({ correctedFile, base64 });
              },
              'image/jpeg',
              0.95
            );
          };

          img.onerror = () => {
            // Fallback на оригинальный файл
            const fallbackReader = new FileReader();
            fallbackReader.onload = (fallbackEvent) => {
              resolve({
                correctedFile: file,
                base64: fallbackEvent.target?.result as string,
              });
            };
            fallbackReader.readAsDataURL(file);
          };

          img.src = e.target?.result as string;
        };

        reader.onerror = (error) => {
          reject(error);
        };

        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  };

  const processImageQuery = useMutation({
    mutationFn: (file: File) => {
      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          Analytics.trackEvent('files-timer', 'end');
          reject(new Error('timeout'));
        }, FILE_PROCESSING_TIMEOUT);

        FileService.createImage(file)
          .then((res) => {
            clearTimeout(timeoutId);
            resolve(res);
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            reject(err);
          });
      });
    },
    onSuccess: async (result: ImageUploadResponse, file) => {
      if (result.data && result.data.url) {
        setShowFaceScan(true);
        setImageUrl(result.data.url);
        const size = await getOriginalImageSize(file);

        processImageFaceLandmarks.mutate({
          file,
          originalWidth: size.width,
          originalHeight: size.height,
        });

        if (!faceScanEnabled) {
          onNext();
        }
        setIsLoading(false);
        setHasError(false);
      }
    },
    onError: (error) => {
      setPhotoError(true);
      setGeneratedPhotoUrl(null);
      setHasError(true);
      setIsLoading(false);
      setFilesError(true);
      console.log(error);

      Analytics.trackEvent('files-upload', 'error');
    },
  });

  const handleFaceScan = async (file: File) => {
    try {
      setShowFaceScan(true);
      processImageQuery.mutate(file);

      Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
        page: 'face-scan',
        answer: 'continue',
      });

      if (!faceScanEnabled) {
        onNext();
      }

      setIsLoading(false);
      setHasError(false);
    } catch (error) {
      setPhotoError(true);
      setGeneratedPhotoUrl(null);
      setHasError(true);
      setIsLoading(false);
      setFilesError(true);
      console.log(error);

      Analytics.trackEvent('files-upload', 'error');
    }
  };

  const processImageFaceLandmarks = useMutation({
    mutationFn: async (data: {
      file: File;
      originalWidth: number;
      originalHeight: number;
    }): Promise<any> => {
      setShowFaceScan(true);

      const response = await AiService.faceLandmarks(data.file);
      return {
        ...response.data,
        originalWidth: data.originalWidth,
        originalHeight: data.originalHeight,
      };
    },
    onSuccess: (result: any) => {
      Analytics.trackEvent('photo_upload', 'success');
      Analytics.trackEvent('face_landmarks', 'received');

      if (result?.landmarks?.length > 0 && result?.boundingBox) {
        setFaceLandmarkData({
          boundingBox: result.boundingBox,
          landmarks: result.landmarks,
        });
        setFaceData({
          boundingBox: result.boundingBox,
          landmarks: result.landmarks,
          originalWidth: result.originalWidth,
          originalHeight: result.originalHeight,
        });
      }

      setPhotoError(false);
      setShowErrorPopUp(false);
      setIsLoading(false);
    },
    onError: (error: any) => {
      setPhotoError(true);
      setHasError(true);
      setFaceData(null);

      let extractedMessage = 'Unknown error';

      const raw = error?.response?.data?.message;

      if (typeof raw === 'string') {
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed?.response?.message) {
              extractedMessage = parsed.response.message;
            } else if (parsed?.message) {
              extractedMessage = parsed.message;
            }
          } catch {
            extractedMessage = raw;
          }
        } else {
          extractedMessage = raw;
        }
      } else if (typeof error.message === 'string') {
        extractedMessage = error.message;
      }

      setFaceLandmarksError(extractedMessage);
      Analytics.trackEvent('face_landmarks', 'error', { error: extractedMessage });
    },
  });

  const handleChooseFromGallery = () => {
    fileInputRef.current?.click();
  };

  const handleOpenSelfieCamera = () => {
    cameraInputRef.current?.click();
  };

  return (
    <div className={'layout ltsv-photo-example_new'}>
      <div className={'back'}>
        {currentIndex !== 0 && (
          <button onClick={onPrev}>
            <BackIcon />
          </button>
        )}
        {isShowedSkipButton && (
          <div
            className={'skip'}
            onClick={() => {
              Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
                page: 'face-scan',
                answer: 'skip',
              });
              onNext();
            }}
          >
            {t('onboarding.lts-photo-example.skip')}
          </div>
        )}
      </div>
      <Title tag='h2' className='title'>
        {getLocalizedText(ltsPhotoExample.title, locale)}
      </Title>
      <div className={'content'}>
        <div className='content-wrapper'>
          <div>
            <Text bold size='m' className='s-title'>
              {t('onboarding.lts-photo-example.subTitle1')}
            </Text>

            <Text size='m' medium className='desc'>
              {t('onboarding.lts-photo-example.description1')}
            </Text>
          </div>
          <div className='items'>
            <img src={image1} alt='' />
            <img src={image2} alt='' />
            <img src={image3} alt='' />
          </div>
        </div>

        <div className='content-wrapper'>
          <div>
            <Text bold size='m' className='s-title'>
              {t('onboarding.lts-photo-example.subTitle2')}
            </Text>

            <Text size='m' medium className='desc'>
              {t('onboarding.lts-photo-example.description2')}
            </Text>
          </div>
          <div className='items'>
            <img src={image4} alt='' />
            <img src={image5} alt='' />
            <img src={image6} alt='' />
          </div>
        </div>
        <div className='overlay_new'></div>
      </div>

      <div className='controls'>
        <Button
          onClick={() => {
            setIsOpen(true);
            Analytics.trackEvent('pick_photo_continue', 'click');
          }}
          //@ts-ignore
          title={
            isLoading ? (
              <LoadingIcon width={20} height={20} />
            ) : (
              t('onboarding.lts-photo-example.continue')
            )
          }
        ></Button>
      </div>

      <input
        ref={fileInputRef}
        type='file'
        id='file-upload'
        accept='image/*'
        onChange={(e) => handleChangeImage(e.target)}
        hidden
      />

      <input
        ref={cameraInputRef}
        type='file'
        id='camera-upload'
        accept='image/*'
        capture='user'
        onChange={(e) => handleChangeImage(e.target)}
        hidden
      />

      {isOpen && (
        <div className='select-photo-popup'>
          <div className='popup-title'>
            <Text semibold>{t('onboarding.faceScanPopup.title')}</Text>
          </div>
          <div onClick={() => setIsOpen(false)} className={'close'}>
            <CloseIcon />
          </div>
          <Text regular size='m' className='error-text'>
            {t('onboarding.lts-photo-example.faceScan.errorSubTitle')}
          </Text>
          <div className='popup-controls'>
            <Button
              title={t('onboarding.faceScanPopup.takePhoto')}
              onClick={() => {
                handleOpenSelfieCamera();
                Analytics.trackEvent('take_a_photo', 'click');
              }}
            />

            <Button
              title={t('onboarding.faceScanPopup.chooseFromGallery')}
              type={'secondary'}
              onClick={() => {
                handleChooseFromGallery();
                Analytics.trackEvent('choose_from_gallery', 'click');
              }}
            />
          </div>
        </div>
      )}

      {showFaceScan && faceScanEnabled && !isLoading && (
        <FaceScanOverlay
          imageUrl={imageBase64}
          isBlurred={isBlurred}
          hideScanLine={hideScanLine}
          isImageLoaded={isImageLoaded}
          showErrorPopUp={showErrorPopUp}
          errorTitle={
            faceLandmarksError
            // t('onboarding.lts-photo-example.faceScan.errorTitle')
          }
          errorDescription={t('onboarding.faceScanAnimation.errorDescription')}
          faceData={faceLandmarkData}
          showLandmarks={showFacePoints}
          onImageLoad={() => setIsImageLoaded(true)}
          onChoosePhoto={() => {
            setShowFaceScan(false);
            setPhotoError(false);
            setShowErrorPopUp(false);
            setFaceLandmarkData(null);
            Analytics.trackEvent('face-scan-choose-photo', 'click');
          }}
          onSkip={() => {
            onNext();
            setImageUrl('');
            setImageBase64('');
            Analytics.trackEvent('face-scan-skip', 'click');
          }}
        />
      )}
    </div>
  );
}

export default Page;
